// src/components/Admin/Common/DetailsModal.tsx

import React from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  IconButton, 
  Typography, 
  Box,
  useTheme,
  Slide,
  Paper,
  Divider,
  DialogActions,
  Button,
  alpha,
  useMediaQuery,
  Avatar
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { TransitionProps } from '@mui/material/transitions';

interface DetailsModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  fullScreen?: boolean;
  showCloseButton?: boolean;
}

// Slide transition for the modal
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const DetailsModal: React.FC<DetailsModalProps> = ({ 
  open, 
  onClose, 
  title, 
  children,
  maxWidth = 'lg',
  fullScreen = false,
  showCloseButton = true
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Use fullScreen for mobile devices if not explicitly set
  const isFullScreen = fullScreen || isMobile;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      fullScreen={isFullScreen}
      TransitionComponent={Transition}
      aria-labelledby="modal-title"
      PaperProps={{
        elevation: 8,
        sx: {
          borderRadius: isFullScreen ? 0 : 2,
          overflow: 'hidden',
          background: alpha(theme.palette.background.paper, 0.98)
        }
      }}
    >
      <DialogTitle
        id="modal-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          boxShadow: theme.shadows[3]
        }}
      >
        <Typography variant="h6" component="div" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
          {title}
        </Typography>
        {showCloseButton && (
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: alpha('#fff', 0.1),
              },
            }}
            size="large"
          >
            <Close />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent 
        sx={{ 
          p: { xs: 2, md: 3 }, 
          overflowY: 'auto' 
        }}
      >
        {children}
      </DialogContent>
      {!showCloseButton && (
        <DialogActions sx={{ p: 2, justifyContent: 'flex-end' }}>
          <Button 
            onClick={onClose} 
            variant="outlined"
            size="large"
            sx={{ 
              borderRadius: '8px',
              px: 3
            }}
          >
            Close
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default DetailsModal;
